import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import NavBar from '../components/NavBar';
import apiClient from '../api/client';
import { useAuth } from '../App';
import { ACTIVITY_TYPES, AWARD_LEVELS, formatDate } from '../utils/helpers';
import useWindowWidth from '../hooks/useWindowWidth';
import { trackEvent } from '../utils/analytics';

const s = {
  page: { minHeight: '100vh', backgroundColor: '#f8fafc' },
  content: { maxWidth: '860px', margin: '0 auto', padding: '2rem 1.5rem' },
  pageTitle: { fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '2rem' },
  // M5 (T42): default desktop padding. Sections get extra marginBottom on
  // mobile (see usage) to give the long single-column scroll visual breaks
  // between Basic Info / Activities / Awards / Subscription / etc.
  section: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '1.75rem',
    marginBottom: '1.5rem',
  },
  sectionTitle: { fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', marginBottom: '1.25rem' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  formGroup: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#1e293b', marginBottom: '0.35rem' },
  // M4 (T42): inputs/selects gain a 44px min-height to comfortably accept
  // a thumb-tap on mobile. The desktop padding stays the same.
  input: {
    width: '100%', padding: '0.6rem 0.875rem', borderRadius: '8px',
    border: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#1e293b', outline: 'none',
    minHeight: '44px',
  },
  select: {
    width: '100%', padding: '0.6rem 0.875rem', borderRadius: '8px',
    border: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#1e293b',
    backgroundColor: '#ffffff', outline: 'none',
    minHeight: '44px',
  },
  textarea: {
    width: '100%', padding: '0.6rem 0.875rem', borderRadius: '8px',
    border: '1px solid #e2e8f0', fontSize: '0.875rem', color: '#1e293b',
    outline: 'none', resize: 'vertical', minHeight: '70px', fontFamily: 'inherit',
  },
  // M4 (T42): primary/secondary action buttons hit 44px.
  btn: {
    padding: '0.6rem 1.25rem', borderRadius: '8px', fontSize: '0.875rem',
    fontWeight: '600', cursor: 'pointer', border: 'none', transition: 'opacity 0.15s',
    minHeight: '44px',
  },
  primaryBtn: { backgroundColor: '#01696F', color: '#ffffff' },
  dangerBtn: { backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' },
  outlineBtn: { backgroundColor: '#ffffff', color: '#01696F', border: '1px solid #01696F' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  divider: { borderTop: '1px solid #e2e8f0', margin: '1.25rem 0' },
  listItem: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.75rem 0', borderBottom: '1px solid #f1f5f9',
  },
  listItemInfo: { flex: 1 },
  listItemName: { fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' },
  listItemMeta: { fontSize: '0.8rem', color: '#64748b', marginTop: '0.15rem' },
  badge: (color) => ({
    display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '999px',
    backgroundColor: color + '1a', color: color, fontSize: '0.75rem', fontWeight: '600',
    marginRight: '0.4rem',
  }),
  emptyMsg: { color: '#94a3b8', fontSize: '0.875rem', padding: '0.5rem 0', fontStyle: 'italic' },
  success: {
    color: '#16a34a', fontSize: '0.875rem', padding: '0.6rem 0.875rem',
    backgroundColor: '#f0fdf4', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #bbf7d0',
  },
  error: {
    color: '#ef4444', fontSize: '0.875rem', padding: '0.6rem 0.875rem',
    backgroundColor: '#fef2f2', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fecaca',
  },
  addFormTitle: { fontSize: '0.9rem', fontWeight: '600', color: '#64748b', marginBottom: '0.75rem' },
};

// T31: small save-state indicator rendered next to the section's Save button.
// `state` is one of: 'idle' | 'saving' | 'saved' | 'error'. `at` is a Date.
function SaveStateIndicator({ state, at }) {
  if (!state || state === 'idle') return null;
  const base = { marginLeft: '0.75rem', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' };
  if (state === 'saving') {
    return <span role="status" aria-live="polite" style={{ ...base, color: '#64748b' }}>Saving…</span>;
  }
  if (state === 'saved') {
    const t = at ? at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '';
    return <span role="status" aria-live="polite" style={{ ...base, color: '#0d9488', fontWeight: 600 }}>✓ Saved{t ? ` ${t}` : ''}</span>;
  }
  if (state === 'error') {
    return <span role="alert" style={{ ...base, color: '#ef4444', fontWeight: 600 }}>Save failed — try again</span>;
  }
  return null;
}

const LEVEL_COLORS = {
  'School': '#6b7280', 'Regional': '#3b82f6', 'State': '#f97316',
  'National': '#a855f7', 'International': '#01696F',
};

const TYPE_COLORS = {
  'Academic': '#3b82f6', 'Athletic': '#f97316', 'Arts': '#a855f7',
  'Community Service': '#22c55e', 'Leadership': '#eab308',
  'Work Experience': '#6b7280', 'Internship': '#06b6d4',
  'Research': '#8b5cf6', 'Personal Growth': '#ec4899', 'Other': '#94a3b8',
};

function BasicInfoSection({ user, onSave }) {
  const { isMobile } = useWindowWidth();
  const [form, setForm] = useState({
    name: user?.name || '',
    school: user?.school || '',
    gradYear: user?.gradYear || '',
    gpaWeighted: user?.gpaWeighted || '',
    gpaUnweighted: user?.gpaUnweighted || '',
    satTotal: user?.satTotal || '',
    actComposite: user?.actComposite || '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // T31: per-section save state ('idle' | 'saving' | 'saved' | 'error').
  const [saveState, setSaveState] = useState({ status: 'idle', at: null });

  useEffect(() => {
    setForm({
      name: user?.name || '',
      school: user?.school || '',
      gradYear: user?.gradYear || '',
      gpaWeighted: user?.gpaWeighted || '',
      gpaUnweighted: user?.gpaUnweighted || '',
      satTotal: user?.satTotal || '',
      actComposite: user?.actComposite || '',
    });
  }, [user]);

  // Clear 'saved' state to 'idle' after 3s. 'error' stays sticky until next save.
  useEffect(() => {
    if (saveState.status !== 'saved') return undefined;
    const id = setTimeout(() => setSaveState({ status: 'idle', at: null }), 3000);
    return () => clearTimeout(id);
  }, [saveState]);

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setMsg(''); setErr('');
    setSaveState({ status: 'saving', at: null });
    try {
      // Filter out empty strings so optional fields aren't sent as invalid values
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== '') payload[k] = v;
      }
      await apiClient.put('/api/profile', payload);
      setMsg('Profile updated successfully.');
      setSaveState({ status: 'saved', at: new Date() });
      onSave();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to update profile.');
      setSaveState({ status: 'error', at: new Date() });
    } finally {
      setSaving(false);
    }
  };

  const grid2Style = isMobile ? { ...s.grid2, gridTemplateColumns: '1fr' } : s.grid2;

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Basic Information</div>
      {msg && <div role="status" style={s.success}>{msg}</div>}
      {err && <div role="alert" style={s.error}>{err}</div>}
      <div style={grid2Style}>
        <div style={s.formGroup}>
          <label style={s.label}>Full Name</label>
          <input style={s.input} value={form.name} onChange={e => handleChange('name', e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>School</label>
          <input style={s.input} value={form.school} onChange={e => handleChange('school', e.target.value)} placeholder="High School Name" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Graduation Year</label>
          <select style={s.select} value={form.gradYear} onChange={e => handleChange('gradYear', e.target.value)}>
            {['2025','2026','2027','2028','2029'].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>GPA (Weighted)</label>
          <input style={s.input} type="number" step="0.01" min="0" max="5" value={form.gpaWeighted} onChange={e => handleChange('gpaWeighted', e.target.value)} placeholder="4.2" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>GPA (Unweighted)</label>
          <input style={s.input} type="number" step="0.01" min="0" max="4" value={form.gpaUnweighted} onChange={e => handleChange('gpaUnweighted', e.target.value)} placeholder="3.8" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>SAT Total</label>
          <input
            style={s.input}
            type="number"
            min="400"
            max="1600"
            step="10"
            value={form.satTotal}
            onChange={e => handleChange('satTotal', e.target.value)}
            placeholder="1200"
          />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>ACT Composite</label>
          <input
            style={s.input}
            type="number"
            min="1"
            max="36"
            value={form.actComposite}
            onChange={e => handleChange('actComposite', e.target.value)}
            placeholder="28"
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={{ ...s.btn, ...s.primaryBtn, ...(saving ? s.btnDisabled : {}) }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
        <SaveStateIndicator state={saveState.status} at={saveState.at} />
      </div>
    </div>
  );
}

function ActivitiesSection({ activities, awards, onRefresh }) {
  const { isMobile } = useWindowWidth();
  const emptyForm = {
    name: '', type: 'Academic', role: '', description: '',
    shortDescription: '', hoursPerWeek: '', weeksPerYear: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [descLoading, setDescLoading] = useState(false);
  const [descError, setDescError] = useState('');
  // T31: per-section save state for activity add.
  const [saveState, setSaveState] = useState({ status: 'idle', at: null });

  useEffect(() => {
    if (saveState.status !== 'saved') return undefined;
    const id = setTimeout(() => setSaveState({ status: 'idle', at: null }), 3000);
    return () => clearTimeout(id);
  }, [saveState]);

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const generateDescription = async () => {
    if (!form.name.trim()) return;
    setDescLoading(true);
    setDescError('');
    try {
      const res = await apiClient.post('/api/ai/activity-description', {
        name: form.name,
        role: form.role,
        type: form.type,
        description: form.description,
        hoursPerWeek: form.hoursPerWeek,
        weeksPerYear: form.weeksPerYear,
      });
      const generated = res.data?.data?.shortDescription || '';
      if (generated) {
        setForm(f => ({ ...f, shortDescription: generated }));
      }
      if (res.data?.error) {
        setDescError("Couldn't generate description. You can write it manually.");
      }
    } catch {
      setDescError("Couldn't generate description. You can write it manually.");
    } finally {
      setDescLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setErr('');
    setSaveState({ status: 'saving', at: null });
    try {
      await apiClient.post('/api/profile/activities', {
        ...form,
        hoursPerWeek: form.hoursPerWeek ? Number(form.hoursPerWeek) : undefined,
        weeksPerYear: form.weeksPerYear ? Number(form.weeksPerYear) : undefined,
      });
      setForm(emptyForm);
      setSaveState({ status: 'saved', at: new Date() });
      onRefresh();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add activity.');
      setSaveState({ status: 'error', at: new Date() });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this activity?')) return;
    try {
      await apiClient.delete(`/api/profile/activities/${id}`);
      onRefresh();
    } catch {
      setErr('Failed to remove activity.');
    }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Activities</div>
      {err && <div role="alert" style={s.error}>{err}</div>}

      {activities.length === 0
        ? <p style={s.emptyMsg}>No activities yet. Add your extracurriculars below.</p>
        : activities.map(a => (
          /* M5 (T42): on mobile, row stacks the info above a full-width
             Remove control so neither gets cramped against the other. */
          <div key={a.id} style={{
            ...s.listItem,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '0.6rem' : '0',
          }}>
            <div style={s.listItemInfo}>
              <div style={s.listItemName}>
                <span style={s.badge(TYPE_COLORS[a.type] || '#94a3b8')}>{a.type}</span>
                {a.name}
              </div>
              <div style={s.listItemMeta}>
                {a.role && <span>{a.role}</span>}
                {a.hoursPerWeek && <span> · {a.hoursPerWeek}h/wk</span>}
                {a.weeksPerYear && <span> · {a.weeksPerYear} wks/yr</span>}
                {a.shortDescription && (
                  <div style={{ marginTop: '0.2rem', fontStyle: 'italic' }}>"{a.shortDescription}"</div>
                )}
                {(() => {
                  const linkedAwards = awards.filter(aw => aw.linkedActivityId === a.id);
                  return linkedAwards.length > 0 ? (
                    <div style={{ marginTop: '0.2rem', fontSize: '0.78rem', color: '#01696F' }}>
                      {linkedAwards.length} linked award{linkedAwards.length > 1 ? 's' : ''}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
            <button
              style={{
                ...s.btn,
                ...s.dangerBtn,
                alignSelf: isMobile ? 'stretch' : 'auto',
              }}
              onClick={() => handleDelete(a.id)}
            >
              Remove
            </button>
          </div>
        ))
      }

      <div style={s.divider} />
      <div style={s.addFormTitle}>Add Activity</div>
      <div style={isMobile ? { ...s.grid2, gridTemplateColumns: '1fr' } : s.grid2}>
        <div style={s.formGroup}>
          <label style={s.label}>Activity Name *</label>
          <input style={s.input} value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Robotics Club" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Type</label>
          <select style={s.select} value={form.type} onChange={e => handleChange('type', e.target.value)}>
            {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Role / Position</label>
          <input style={s.input} value={form.role} onChange={e => handleChange('role', e.target.value)} placeholder="Team Captain" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Hours per Week</label>
          <input style={s.input} type="number" min="0" value={form.hoursPerWeek} onChange={e => handleChange('hoursPerWeek', e.target.value)} placeholder="5" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Weeks per Year</label>
          <input style={s.input} type="number" min="0" value={form.weeksPerYear} onChange={e => handleChange('weeksPerYear', e.target.value)} placeholder="40" />
        </div>
      </div>
      <div style={{ ...s.formGroup }}>
        <label style={s.label}>Description</label>
        <textarea style={s.textarea} value={form.description} onChange={e => handleChange('description', e.target.value)} placeholder="Brief description of your role and impact..." />
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>
          <span>Short Description</span>
          <span style={{ fontSize: '0.78rem', color: form.shortDescription.length > 150 ? '#ef4444' : '#94a3b8' }}>
            {form.shortDescription.length}/150
          </span>
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <textarea
            style={{ ...s.textarea, minHeight: '55px', flex: 1 }}
            value={form.shortDescription}
            onChange={e => handleChange('shortDescription', e.target.value.slice(0, 150))}
            placeholder="150-char Common App description (e.g., 'Led 12-member robotics team...')"
            maxLength={150}
          />
          {/* T34: Standardized secondary button — outlined teal, matches MfaSection Cancel.
              Was previously a muted gray/solid mix inconsistent with other inline actions. */}
          <button
            type="button"
            onClick={generateDescription}
            disabled={descLoading || !form.name.trim()}
            style={{
              ...s.btn,
              ...s.outlineBtn,
              padding: '0.5rem 0.9rem',
              fontSize: '0.8rem',
              cursor: descLoading || !form.name.trim() ? 'not-allowed' : 'pointer',
              opacity: descLoading || !form.name.trim() ? 0.6 : 1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {descLoading ? 'Generating...' : 'Generate'}
          </button>
        </div>
        {descError && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>{descError}</div>}
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.25rem' }}>Used for Common App. Max 150 characters.</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          style={{ ...s.btn, ...s.primaryBtn, ...(saving ? s.btnDisabled : {}) }}
          onClick={handleAdd}
          disabled={saving}
        >
          {saving ? 'Adding...' : 'Add Activity'}
        </button>
        <SaveStateIndicator state={saveState.status} at={saveState.at} />
      </div>
    </div>
  );
}

function AwardsSection({ awards, activities, onRefresh }) {
  const { isMobile } = useWindowWidth();
  const emptyForm = { name: '', level: 'School', date: '', description: '', linkedActivityId: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // T31: per-section save state for award add.
  const [saveState, setSaveState] = useState({ status: 'idle', at: null });

  useEffect(() => {
    if (saveState.status !== 'saved') return undefined;
    const id = setTimeout(() => setSaveState({ status: 'idle', at: null }), 3000);
    return () => clearTimeout(id);
  }, [saveState]);

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setErr('');
    setSaveState({ status: 'saving', at: null });
    try {
      await apiClient.post('/api/profile/awards', {
        ...form,
        linkedActivityId: form.linkedActivityId || null,
      });
      setForm(emptyForm);
      setSaveState({ status: 'saved', at: new Date() });
      onRefresh();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add award.');
      setSaveState({ status: 'error', at: new Date() });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this award?')) return;
    try {
      await apiClient.delete(`/api/profile/awards/${id}`);
      onRefresh();
    } catch {
      setErr('Failed to remove award.');
    }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Awards & Honors</div>
      {err && <div role="alert" style={s.error}>{err}</div>}

      {awards.length === 0
        ? <p style={s.emptyMsg}>No awards yet. Add your achievements below.</p>
        : awards.map(a => (
          /* M5 (T42): mirror Activities — stack info + Remove on mobile. */
          <div key={a.id} style={{
            ...s.listItem,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '0.6rem' : '0',
          }}>
            <div style={s.listItemInfo}>
              <div style={s.listItemName}>
                <span style={s.badge(LEVEL_COLORS[a.level] || '#94a3b8')}>{a.level}</span>
                {a.name}
              </div>
              <div style={s.listItemMeta}>
                {a.date && <span>{formatDate(a.date)}</span>}
                {a.linkedActivityId && (() => {
                  const linked = activities.find(act => act.id === a.linkedActivityId);
                  return linked ? (
                    <span style={{ marginLeft: a.date ? '0.5rem' : 0 }}>
                      · Linked to <span style={{ fontWeight: '600' }}>{linked.name}</span>
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
            <button
              style={{
                ...s.btn,
                ...s.dangerBtn,
                alignSelf: isMobile ? 'stretch' : 'auto',
              }}
              onClick={() => handleDelete(a.id)}
            >
              Remove
            </button>
          </div>
        ))
      }

      <div style={s.divider} />
      <div style={s.addFormTitle}>Add Award</div>
      <div style={isMobile ? { ...s.grid2, gridTemplateColumns: '1fr' } : s.grid2}>
        <div style={s.formGroup}>
          <label style={s.label}>Award Name *</label>
          <input style={s.input} value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Science Fair 1st Place" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Level</label>
          <select style={s.select} value={form.level} onChange={e => handleChange('level', e.target.value)}>
            {AWARD_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Date</label>
          <input style={s.input} type="date" value={form.date} onChange={e => handleChange('date', e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Linked Activity</label>
          <select
            style={s.select}
            value={form.linkedActivityId}
            onChange={e => handleChange('linkedActivityId', e.target.value)}
          >
            <option value="">None</option>
            {activities.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>Description</label>
        <textarea style={s.textarea} value={form.description} onChange={e => handleChange('description', e.target.value)} placeholder="Brief description of this award..." />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          style={{ ...s.btn, ...s.primaryBtn, ...(saving ? s.btnDisabled : {}) }}
          onClick={handleAdd}
          disabled={saving}
        >
          {saving ? 'Adding...' : 'Add Award'}
        </button>
        <SaveStateIndicator state={saveState.status} at={saveState.at} />
      </div>
    </div>
  );
}

function NotificationsSection({ user, onSave }) {
  const [optedOut, setOptedOut] = useState(Boolean(user?.reminderOptOut));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Sync local toggle state if the user prop refreshes (e.g. after fetchProfile).
  useEffect(() => { setOptedOut(Boolean(user?.reminderOptOut)); }, [user?.reminderOptOut]);

  const handleToggle = async () => {
    const next = !optedOut;
    setSaving(true); setMsg(''); setErr('');
    setOptedOut(next); // optimistic
    try {
      await apiClient.put('/api/profile', { reminderOptOut: next });
      setMsg(next ? 'Weekly reminders turned off.' : 'Weekly reminders turned on.');
      onSave && onSave();
    } catch (e) {
      setOptedOut(!next); // revert
      setErr(e.response?.data?.error || 'Failed to update preference.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.section}>
      <h2 style={s.sectionTitle}>Email Notifications</h2>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '1rem', flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.25rem' }}>Weekly journaling reminder</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.45 }}>
            A Sunday email with your current streak and a nudge to log this week. You can turn it off anytime.
          </div>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={saving}
          aria-pressed={!optedOut}
          aria-label={optedOut ? 'Turn weekly reminders on' : 'Turn weekly reminders off'}
          style={{
            position: 'relative',
            width: '52px', height: '30px',
            borderRadius: '999px',
            border: 'none',
            backgroundColor: optedOut ? '#cbd5e1' : '#01696F',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
            transition: 'background-color 0.15s',
            flexShrink: 0,
          }}
        >
          <span style={{
            position: 'absolute', top: '3px',
            left: optedOut ? '3px' : '25px',
            width: '24px', height: '24px',
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            transition: 'left 0.15s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>

      {msg && <p role="status" style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#16a34a' }}>{msg}</p>}
      {err && <p role="alert" style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#ef4444' }}>{err}</p>}
    </div>
  );
}

function MfaSection() {
  const { isMobile } = useWindowWidth();
  const [mfaStatus, setMfaStatus] = useState('loading'); // loading | unavailable | disabled | enabled
  const [setupUri, setSetupUri] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    checkMfaStatus();
  }, []);

  const checkMfaStatus = async () => {
    try {
      const { fetchMFAPreference } = await import('aws-amplify/auth');
      const prefs = await fetchMFAPreference();
      if (prefs.preferred === 'TOTP' || (prefs.enabled && prefs.enabled.includes('TOTP'))) {
        setMfaStatus('enabled');
      } else {
        setMfaStatus('disabled');
      }
    } catch (e) {
      // If the call fails, MFA is likely not configured on the Cognito pool
      setMfaStatus('unavailable');
    }
  };

  const handleEnableMfa = async () => {
    setErr('');
    setMsg('');
    try {
      const { setUpTOTP } = await import('aws-amplify/auth');
      const totpSetup = await setUpTOTP();
      const uri = totpSetup.getSetupUri('BragSheet');
      setSetupUri(uri.toString());
    } catch (e) {
      if (e.message && (e.message.includes('not enabled') || e.message.includes('not available'))) {
        setErr('MFA is not enabled on this account pool. Ask your administrator to enable TOTP MFA in Cognito.');
      } else {
        setErr(e.message || 'Failed to start MFA setup.');
      }
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    setVerifyLoading(true);
    try {
      const { verifyTOTPSetup, updateMFAPreference } = await import('aws-amplify/auth');
      await verifyTOTPSetup({ code: verifyCode });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMsg('MFA enabled successfully! Your account is now protected with two-factor authentication.');
      setMfaStatus('enabled');
      setSetupUri('');
      setVerifyCode('');
    } catch (e) {
      setErr(e.message || 'Invalid code. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!window.confirm('Disable two-factor authentication? This will make your account less secure.')) return;
    setErr('');
    setMsg('');
    try {
      const { updateMFAPreference } = await import('aws-amplify/auth');
      await updateMFAPreference({ totp: 'DISABLED' });
      setMsg('MFA has been disabled.');
      setMfaStatus('disabled');
    } catch (e) {
      setErr(e.message || 'Failed to disable MFA.');
    }
  };

  if (mfaStatus === 'loading') {
    return (
      <div style={s.section}>
        <div style={s.sectionTitle}>Two-Factor Authentication (MFA)</div>
        <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Checking MFA status...</p>
      </div>
    );
  }

  if (mfaStatus === 'unavailable') {
    return (
      <div style={s.section}>
        <div style={s.sectionTitle}>Two-Factor Authentication (MFA)</div>
        <div style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          color: '#92400e',
          borderRadius: '8px',
          padding: '0.75rem',
          fontSize: '0.875rem',
        }}>
          MFA is not currently available. The Cognito User Pool must be configured to support TOTP MFA.
          See <code>docs/setup-mfa.md</code> for configuration instructions.
        </div>
      </div>
    );
  }

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Two-Factor Authentication (MFA)</div>
      {msg && <div role="status" style={s.success}>{msg}</div>}
      {err && <div role="alert" style={s.error}>{err}</div>}

      {mfaStatus === 'enabled' && !setupUri && (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
          }}>
            <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '0.9rem' }}>Active</span>
            <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Your account is protected with TOTP two-factor authentication.
            </span>
          </div>
          <button
            style={{ ...s.btn, ...s.dangerBtn }}
            onClick={handleDisableMfa}
          >
            Disable MFA
          </button>
        </div>
      )}

      {mfaStatus === 'disabled' && !setupUri && (
        <div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Add an extra layer of security to your account. You will need an authenticator app
            (Google Authenticator, Authy, 1Password, etc.) to generate verification codes.
          </p>
          <button
            style={{ ...s.btn, ...s.primaryBtn }}
            onClick={handleEnableMfa}
          >
            Enable MFA
          </button>
        </div>
      )}

      {setupUri && (
        <div>
          <p style={{ color: '#1e293b', fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.75rem' }}>
            Scan this QR code with your authenticator app:
          </p>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1.5rem',
            marginBottom: '1rem',
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
          }}>
            <QRCodeSVG value={setupUri} size={200} level="M" />
          </div>

          <details style={{ marginBottom: '1rem' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#64748b' }}>
              Can't scan? Show manual setup key
            </summary>
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '0.75rem',
              marginTop: '0.5rem',
              wordBreak: 'break-all',
              fontSize: '0.8rem',
              color: '#475569',
              fontFamily: 'monospace',
            }}>
              {setupUri}
            </div>
          </details>

          <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1rem' }}>
            After scanning, enter the 6-digit code from your authenticator app to verify.
          </p>

          <form onSubmit={handleVerifyMfa}>
            <div style={s.formGroup}>
              <label style={s.label}>Verification Code</label>
              <input
                style={{ ...s.input, maxWidth: isMobile ? '100%' : '200px' }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                required
                autoFocus
                autoComplete="one-time-code"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="submit"
                style={{ ...s.btn, ...s.primaryBtn, ...(verifyLoading ? s.btnDisabled : {}) }}
                disabled={verifyLoading}
              >
                {verifyLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
              <button
                type="button"
                style={{ ...s.btn, ...s.outlineBtn }}
                onClick={() => { setSetupUri(''); setVerifyCode(''); setErr(''); }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SubscriptionSection() {
  const { subscriptionStatus } = useAuth();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const status = subscriptionStatus?.status || 'free';
  const plan = subscriptionStatus?.plan || 'free';
  const renewalDate = subscriptionStatus?.renewalDate || subscriptionStatus?.currentPeriodEnd;
  const daysLeft = subscriptionStatus?.trialDaysLeft;

  const getPlanLabel = () => {
    if (status === 'active') return 'Pro';
    if (status === 'trialing') return 'Pro (Trial)';
    return 'Free';
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await apiClient.post('/api/subscription/customer-portal');
      if (res.data?.data?.url) {
        window.location.href = res.data.data.url;
      } else {
        setErr('Could not open subscription management portal.');
      }
    } catch {
      setErr('Failed to open subscription portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isPro = status === 'active';
  const isTrial = status === 'trialing';

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Subscription</div>
      {err && <div role="alert" style={s.error}>{err}</div>}

      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        marginBottom: '1rem',
      }}>
        <span style={{
          display: 'inline-block',
          padding: '0.2rem 0.65rem',
          borderRadius: '999px',
          fontSize: '0.8rem',
          fontWeight: '600',
          backgroundColor: isPro ? '#f0fdf4' : isTrial ? '#fffbeb' : '#f1f5f9',
          color: isPro ? '#16a34a' : isTrial ? '#92400e' : '#64748b',
          border: `1px solid ${isPro ? '#bbf7d0' : isTrial ? '#fde68a' : '#e2e8f0'}`,
        }}>
          {getPlanLabel()}
        </span>
      </div>

      {isPro && renewalDate && (
        <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
          Renews on <strong>{new Date(renewalDate).toLocaleDateString()}</strong>
        </p>
      )}

      {isTrial && daysLeft != null && (
        <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '1rem' }}>
          {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining in your free trial
        </p>
      )}

      {isPro ? (
        <button
          style={{ ...s.btn, ...s.outlineBtn, ...(loading ? s.btnDisabled : {}) }}
          onClick={handleManageSubscription}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Manage Subscription'}
        </button>
      ) : (
        <a href="/pricing" style={{
          ...s.btn,
          ...s.primaryBtn,
          display: 'inline-block',
          textDecoration: 'none',
          textAlign: 'center',
        }}>
          Upgrade to Pro
        </a>
      )}
    </div>
  );
}

function DangerZoneSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    setErr('');
    try {
      await apiClient.delete('/api/profile/account');
      await logout();
      navigate('/login');
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ ...s.section, borderColor: '#fecaca' }}>
      <div style={{ ...s.sectionTitle, color: '#ef4444' }}>Danger Zone</div>
      {err && <div role="alert" style={s.error}>{err}</div>}
      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Permanently delete your account and all associated data. This action cannot be undone.
      </p>
      {!showConfirm ? (
        <button
          style={{ ...s.btn, ...s.dangerBtn }}
          onClick={() => setShowConfirm(true)}
        >
          Delete Account
        </button>
      ) : (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: '600' }}>
            Type DELETE to confirm account deletion:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            style={{
              width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px',
              border: '1px solid #fecaca', fontSize: '0.9rem', marginBottom: '0.5rem',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              style={{ ...s.btn, ...s.dangerBtn, ...(deleting || confirmText !== 'DELETE' ? s.btnDisabled : {}) }}
              onClick={handleDeleteAccount}
              disabled={deleting || confirmText !== 'DELETE'}
            >
              {deleting ? 'Deleting...' : 'Permanently Delete Account'}
            </button>
            <button
              style={{ ...s.btn, backgroundColor: '#ffffff', color: '#64748b', border: '1px solid #e2e8f0' }}
              onClick={() => { setShowConfirm(false); setConfirmText(''); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { isMobile } = useWindowWidth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchProfile = async () => {
    try {
      const res = await apiClient.get('/api/profile');
      setProfile(res.data.data);
    } catch {
      setError('Failed to load profile.');
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportProfile = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/api/export/report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bragsheet-report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      // exported_pdf: fires only after the download is triggered successfully.
      trackEvent('exported_pdf');
    } catch {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  return (
    <div style={s.page}>
      <NavBar />
      {/* M5 (T42): tighter horizontal padding on mobile gives form fields
          more breathing room without forcing a full-width edge bleed. */}
      <div id="main-content" tabIndex={-1} style={{
        ...s.content,
        padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
      }}>
        {/* M5 (T42): on mobile, stack title above the Export CTA so the CTA
            can render full-width and remain prominent (T33 elevation). */}
        <div style={{
          display: 'flex',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          gap: '1rem',
          flexWrap: 'wrap',
        }}>
          <h1 style={{ ...s.pageTitle, marginBottom: 0 }}>Your Profile</h1>
          {/* T33: Elevated Pro CTA — filled teal, larger padding/text, contextual subtext.
              Access control is enforced server-side via requirePro middleware on /api/export/report.
              M5 (T42): full-width on mobile so it's an unmistakable tap target. */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'stretch' : 'flex-end',
            gap: '0.35rem',
            width: isMobile ? '100%' : 'auto',
          }}>
            <button
              style={{
                ...s.btn,
                ...s.primaryBtn,
                padding: '0.85rem 1.5rem',
                fontSize: '0.95rem',
                minHeight: '48px',
                width: isMobile ? '100%' : 'auto',
                boxShadow: '0 1px 2px rgba(1, 105, 111, 0.15)',
                ...((loading || !!error || exporting) ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
              }}
              onClick={handleExportProfile}
              disabled={loading || !!error || exporting}
            >
              {exporting ? 'Generating...' : 'Export PDF Report'}
            </button>
            <div style={{
              fontSize: '0.78rem',
              color: '#64748b',
              maxWidth: isMobile ? 'none' : '260px',
              textAlign: isMobile ? 'left' : 'right',
            }}>
              Generate a shareable PDF for advisors and parents.
            </div>
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748b', padding: '3rem' }}>Loading profile...</p>
        ) : error ? (
          <div role="alert" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
            <p style={{ marginBottom: '0.75rem' }}>{error}</p>
            <button onClick={fetchProfile} style={{ ...s.btn, ...s.primaryBtn, padding: '0.5rem 1rem' }}>Retry</button>
          </div>
        ) : (
          /* M5 (T42): on mobile, render a thin divider with extra vertical
             room between sections so the long single-column scroll has clear
             section breaks (Basic Info / Activities / Awards / etc). */
          <div style={isMobile ? { display: 'flex', flexDirection: 'column', gap: '1.25rem' } : undefined}>
            <BasicInfoSection user={profile?.user} onSave={fetchProfile} />
            {isMobile && <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '0 0.25rem' }} aria-hidden="true" />}
            <ActivitiesSection activities={profile?.activities || []} awards={profile?.awards || []} onRefresh={fetchProfile} />
            {isMobile && <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '0 0.25rem' }} aria-hidden="true" />}
            <AwardsSection awards={profile?.awards || []} activities={profile?.activities || []} onRefresh={fetchProfile} />
            {isMobile && <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '0 0.25rem' }} aria-hidden="true" />}
            <SubscriptionSection />
            <NotificationsSection user={profile?.user} onSave={fetchProfile} />
            <MfaSection />
            <DangerZoneSection />
          </div>
        )}
      </div>
    </div>
  );
}
